import Prescription from "../../components/patient-health/doctor-notes/prescription-page";
import React, { useRef, useState, useEffect } from "react";
import { Box, VStack, HStack } from "@chakra-ui/react";
import { useReactToPrint } from "react-to-print";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { useLocation } from "react-router-dom";
import moment from "moment";
import {
  GetDoctorNotes,
  UploadPrescription,
} from "../../middleware/services/visits/patient-overview/updatedoctornotes";
import { PrimaryButton } from "../../components";
import Loader from "../../components/loader/loader";
import useLoader from "../../components/loader/loaderstates";
import { COLORS } from "../../components/styles/colors";
import {
  extractDiagnosis,
  formatMedicalHistory,
  transformVitalData,
  calculateAge,
  formatMedicines,
  formatOrders,
  formatGeneralNotes,
  formatDieticianNotes,
  transformInvestigationData,
  transformFetchedDocuments,
  processROSData,
} from "../../middleware/domains/visits/patient-health/prescriptions";
import { formatDate_Do_MMM_YYYY, uploadImageCommonFunction } from "../../auth";
import { useNavigate } from "react-router-dom";
import { config } from "@fullcalendar/core/internal";
import { CONFIG } from "../../appconfig/appconfig";
import processPhysicalExamData from "../../middleware/domains/visits/patient-health/physical-exam";
import { GetPatientHealthData } from "../../middleware/services/visits/patient-overview";
import generatePDF, { Resolution, Margin, Options } from "react-to-pdf";
import VisitSummary from "../../components/patient-health/doctor-notes/summary-page";

const VisitPrescription = () => {
  //-------Fetch Parameters

  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const patientId = params.get("patientId");
  const appointmentId = params.get("appointmentId");
  const doctorId = params.get("doctorId");
  const fromEndVisit = Boolean(params.get("fromEndVisit"));
  const isSign = params.get("isSigned") === "true";
  // console.log(fromEndVisit);

  const [inputToPrescription, setInputToPrescription] = useState(null);

  const { isLoading, showLoader, dismissLoader } = useLoader();

  // Safely read LoginInfo from localStorage for base URL (used when uploading prescription PDF)
  let BlobURL = null;
  try {
    const jsonData = localStorage.getItem("LoginInfo");
    if (jsonData) {
      const item = JSON.parse(jsonData);
      BlobURL = item?.imagepath || null;
    }
  } catch (e) {
    console.error("Failed to parse LoginInfo from localStorage:", e);
    BlobURL = null;
  }
  //--------------------
  const fetchDoctorNotes = async () => {
    const inputToFetch = {
      input: {
        patientid: patientId,
        doctorid: doctorId,
        appointmentid: appointmentId,
      },
    };
    showLoader();
    try {
      // Sleep for 10 seconds
      // await new Promise((resolve) => setTimeout(resolve, 15000));

      const response = await GetDoctorNotes(inputToFetch.input);
      const data = response?.output?.data;

      const addressDetails = data?.info?.hospitaladdress;
      let fullAddress = '-';

      if (addressDetails) {
        const {
          address,
          address2,
          city,
          STATE,
          postalcode,
          country
        } = addressDetails;

        if (address || address2 || city || STATE || postalcode || country) {
          fullAddress = [
            address && `${address}`,
            address2 && `${address2}`,
            city && `${city}`,
            STATE && `${STATE}`,
            postalcode && `${postalcode}`,
            country && `${country}`
          ]
            .filter(Boolean)
            .join(', ');
        }
      }

      const fetchedData = {
        drName: data?.info?.drname,
        hospitalLogo: data?.info?.hospitallogo,
        doctorQualification: data?.info?.doctorqualification,
        doctorSpeciality: data?.info?.doctorspeciality,
        doctorPhone: data?.info?.doctorphone,
        doctorPhoneCountryCode: data?.info?.doctorcountrycode,
        doctorMail: data?.info?.doctormail,
        // hospitalAddress: data?.info?.hospitaladdress?.address,
        hospitalAddress: fullAddress,
        patientBookingId: data?.info?.bookingid,
        patientmrnnumber: data?.info?.patientmrn,
        patientName: data?.info?.patientname,
        patientGender: data?.info?.patientgender
          ? data.info.patientgender.charAt(0).toUpperCase() +
          data.info.patientgender.slice(1)
          : null,
        patientAge: calculateAge(data?.info?.patientage),
        patientPhone: data?.info?.patientphone,
        patientPhoneCountryCode: data?.info?.patientcountrycode,
        appointmentDate: data?.info?.appointmentdate
          ? moment(data?.info?.appointmentdate).format("D MMM YYYY")
          : "-",
        settings: data.healthviewsetting || {},
        hpi: data.historyofpresentillness|| [],
        hpitext: data?.hpitext?.description,
        ros: processROSData(data.reviewofsystem) || [],    
        vitalData: transformVitalData(data.vitals || []),
        investigationData: transformInvestigationData(data.pinvestigations || []),
        chiefcomplaint: data?.chiefcomplaint?.chiefcomplaint,
        patientdiagnosis: data?.patientdiagnosis,
        diagnosisData: extractDiagnosis(data.doctorsassessment || []),
        medicalHistory: formatMedicalHistory(data.medicalhistory || []),
        prescribedMedicines: formatMedicines(data.orders.medication || []),
        scaleOut:
          formatMedicines(data.orders.medication || []).length > 5
            ? true
            : false,
        recommendedTests: formatOrders(data.orders || []),
        followUpDate: data?.info?.followupdate
          ? moment(data.info.followupdate).format("D MMM YYYY")
          : "-",
        referralDoctor: data.info.referraldoctor
          ? data?.info?.referraldoctor
          : "-",
        referralDoctorSpeciality: data.info.referraldoctorSpeciality
          ? data?.info?.referraldoctorSpeciality
          : "-",
        // doctorSignImage: null,
        doctorSignImage: isSign && isSign === true ? data?.info?.drname : null,
        time: isSign && isSign === true ? formatDate_Do_MMM_YYYY(data?.time) : null,
        followupDetail: data.followupdetail,
        generalnotes: formatGeneralNotes(data.generalnotes || []),
        dieticiannotes: formatDieticianNotes(data.dieticiannote || []),
        documents: transformFetchedDocuments(data.documentdetail || []),
        templateid: data.templateid? data.templateid : 1,
        customtemplatevalues: data.templatevalue,
        physicalexam: processPhysicalExamData(data.physicalexam) || [],            
      };
      //IMPORTANT: IF ANYTHING IS GETTING CHANGED OR ADDED IN ABOVE DATA PLEASE DO THE SAME CHANGE IN "generate-pdf/index" FILE TOO FOR MOBILE PRESCRIPTION TO BE IN SYNC.
      setInputToPrescription(fetchedData);
    } catch (error) {
      console.error("Error fetching Doctor Notes data:", error);
    } finally {
      dismissLoader();
    }
  };

  useEffect(() => {
    fetchDoctorNotes();
  }, [patientId, doctorId, appointmentId]);

  const componentRef = useRef();

  // const handlePrint = useReactToPrint({
  //   content: () => componentRef.current,
  //   pageStyle: `
  //     @page {
  //       size: A4;
  //       margin: 0;
  //     }
  //     @media print {
  //       body {
  //         -webkit-print-color-adjust: exact;
  //         margin: 0;
  //       }
  //       .page-break {
  //         display: block;
  //         page-break-before: always;
  //       }
  //     }
  //   `,
  // });

  const handlePrint = async () => {
    const elements = document.querySelectorAll(".printPdf"); // Select all elements with the specified class
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    for (let i = 0; i < elements.length; i++) {
      const element = elements[i];

      // Generate a canvas for the current element
      const canvas = await html2canvas(element, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL("image/jpeg", 1);

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width; // Scale the height proportionally

      if (i > 0) {
        pdf.addPage(); // Add a new page for every div after the first
      }

      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
    }

    // Get the PDF as a Blob URL
    const pdfBlobUrl = pdf.output("bloburl");

    // Open the print dialog for the generated PDF
    const printWindow = window.open(pdfBlobUrl);
    if (printWindow) {
      printWindow.focus();
      printWindow.print();
    }
  };
  const handleDownloadLocal = async () => {
    const elements = document.querySelectorAll(".printPdf"); // Select all elements with the specified class
    const Esignature = document.querySelector(".Esignature");
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    for (let i = 0; i < elements.length; i++) {
      const element = elements[i];

      // Generate a canvas for the current element
      const canvas = await html2canvas(element, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL("image/jpeg", 1);

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width; // Scale the height proportionally

      if (i > 0) {
        pdf.addPage(); // Add a new page for every div after the first
      }

      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
    }
    if(Esignature != null){
      Esignature.style.marginTop = "-0.875rem"
      Esignature.style.marginTop = "0"
    }
    
    pdf.save("download.pdf");
    
  };

  const handleDownload = async () => {
    const elements = document.querySelectorAll(".printPdf"); // Select all elements with the specified class
    const Esignature = document.querySelector(".Esignature");
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    for (let i = 0; i < elements.length; i++) {
      const element = elements[i];

      // Generate a canvas for the current element
      await document.fonts.ready;
      const canvas = await html2canvas(element, { scale: 2, useCORS: true });
      const imgData = canvas.toDataURL("image/jpeg", 1); // Use high-quality JPEG

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width; // Scale the height proportionally

      if (i > 0) {
        pdf.addPage(); // Add a new page for every div after the first
      }

      pdf.addImage(imgData, "JPEG", 0, 0, imgWidth, imgHeight);
    }
    if(Esignature != null){
      Esignature.style.marginTop = "-0.875rem"
      Esignature.style.marginTop = "0"
    }
    const pdfBlob = pdf.output("blob"); // Generate the PDF as a Blob
    return pdfBlob; // Return the Blob
  };

  const savePrescriptionUpload = async (fileName) => {
    const inputToSave = {
      input: {
        patientid: patientId,
        doctorid: doctorId,
        appointmentid: appointmentId,
        prescriptionurl: fileName,
        baseurl: BlobURL,
        doctorname: inputToPrescription.drName,
        patientname: inputToPrescription.patientName,
        patientmobile: inputToPrescription.patientPhone,
        patientmobilecountrycode: inputToPrescription.patientPhoneCountryCode
      },
    };
    // console.log(inputToSave);
    try {
      const response = await UploadPrescription(inputToSave.input);
      // console.log(response);
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };

  const uploadFile = async () => {
    try {
      // If we don't yet have basic prescription data (first-time user / missing doctor notes),
      // skip upload instead of throwing and breaking the page.
      if (!inputToPrescription) {
        console.warn(
          "Skipping prescription upload because inputToPrescription is not ready yet."
        );
        return;
      }

      const pdfBlob = await handleDownload();
      const file = new File([pdfBlob], "download.pdf", {
        type: "application/pdf",
      });
      const response = await uploadImageCommonFunction(file, "download.pdf");
      // console.log(response);
      if (response) {
        savePrescriptionUpload(response.name);
      }
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };

  // prescriptionForAppointmentId is stored to disable resubmitting on refresh
  // useEffect(() => {
  //   if (fromEndVisit) {
  //     const storedAppointmentId = localStorage.getItem(
  //       "prescriptionForAppointmentId"
  //     );
  //     if (storedAppointmentId !== appointmentId) {
  //       setTimeout(() => {
  //         // console.log("Upload called");
  //         uploadFile();
  //         localStorage.setItem("prescriptionForAppointmentId", appointmentId);
  //       }, 2000); // Delay of 2 seconds
  //     }
  //   }
  // }, [fromEndVisit, appointmentId]);

  // useEffect(() => {
  //   if (fromEndVisit) {
  //     const storedAppointmentId = localStorage.getItem(
  //       "prescriptionForAppointmentId"
  //     );
  //     if (storedAppointmentId !== appointmentId) {
  //       const uploadAfterFetch = async () => {
  //         await fetchDoctorNotes();
  //         console.log("Upload called");
  //         uploadFile();
  //         localStorage.setItem("prescriptionForAppointmentId", appointmentId);
  //       };
  //       uploadAfterFetch();
  //     }
  //   }
  // }, [fromEndVisit, appointmentId]);

  useEffect(() => {
    if (fromEndVisit) {
      const storedAppointmentId = localStorage.getItem(
        "prescriptionForAppointmentId"
      );
      if (storedAppointmentId !== appointmentId) {
        const uploadAfterFetch = async () => {
          await fetchDoctorNotes();
        };
        uploadAfterFetch();
      }
    }
  }, [fromEndVisit, appointmentId]);

  useEffect(() => {
    if (fromEndVisit && inputToPrescription) {
      const storedAppointmentId = localStorage.getItem(
        "prescriptionForAppointmentId"
      );
      if (storedAppointmentId !== appointmentId) {
         //console.log("Upload called");
        uploadFile();
        localStorage.setItem("prescriptionForAppointmentId", appointmentId);
      }
    }
  }, [fromEndVisit, appointmentId, inputToPrescription]);

  const navigate = useNavigate();

  const handleProgressNotesClick = () => {
    const url = `/visitsummary?doctorId=${doctorId}&patientId=${patientId}&appointmentId=${appointmentId}&fromEndVisit=true`;
    // navigate(url);
    window.open(url, "_blank");
  };


  // progress notes functioanlity --------------------------------------
  const downloadOptions = {
      filename: "Progress Notes.pdf",
      method: "save",
      // default is Resolution.MEDIUM = 3, which should be enough, higher values
      // increases the image quality but also the size of the PDF, so be careful
      // using values higher than 10 when having multiple pages generated, it
      // might cause the page to crash or hang.
      resolution: Resolution.LOW,
      page: {
        // margin is in MM, default is Margin.NONE = 0
        margin: Margin.SMALL,
        // default is 'A4'
        // format: "letter",
        format: "A4",
        // default is 'portrait'
        // orientation: "landscape"
        orientation: "portrait",
      },
      canvas: {
        // default is 'image/jpeg' for better size performance
        mimeType: "image/jpeg",
        qualityRatio: 1,
      },
      // Customize any value passed to the jsPDF instance and html2canvas
      // function. You probably will not need this and things can break,
      // so use with caution.
      overrides: {
        // see https://artskydj.github.io/jsPDF/docs/jsPDF.html for more options
        pdf: {
          compress: true,
        },
        // see https://html2canvas.hertzen.com/configuration for more options
        canvas: {
          useCORS: true,
        },
      },
    };

  const getTargetElement = () => document.getElementById("container");
  
    const downloadPdf = () => generatePDF(getTargetElement, downloadOptions);
  
    const printOptions = {
      filename: "Progress Notes.pdf",
      method: "open",
      // default is Resolution.MEDIUM = 3, which should be enough, higher values
      // increases the image quality but also the size of the PDF, so be careful
      // using values higher than 10 when having multiple pages generated, it
      // might cause the page to crash or hang.
      resolution: Resolution.NORMAL,
      page: {
        // margin is in MM, default is Margin.NONE = 0
        margin: Margin.SMALL,
        // default is 'A4'
        // format: "letter",
        format: "A4",
        // default is 'portrait'
        // orientation: "landscape"
        orientation: "portrait",
      },
      canvas: {
        // default is 'image/jpeg' for better size performance
        mimeType: "image/jpeg",
        qualityRatio: 1,
      },
      // Customize any value passed to the jsPDF instance and html2canvas
      // function. You probably will not need this and things can break,
      // so use with caution.
      overrides: {
        // see https://artskydj.github.io/jsPDF/docs/jsPDF.html for more options
        pdf: {
          compress: true,
        },
        // see https://html2canvas.hertzen.com/configuration for more options
        canvas: {
          useCORS: true,
        },
      },
    };
    const printPdf = () => generatePDF(getTargetElement, printOptions);
  
    const uploadOptions = {
      filename: "ProgressNotes.pdf",
      method: "build",
      // default is Resolution.MEDIUM = 3, which should be enough, higher values
      // increases the image quality but also the size of the PDF, so be careful
      // using values higher than 10 when having multiple pages generated, it
      // might cause the page to crash or hang.
      resolution: Resolution.LOW,
      page: {
        // margin is in MM, default is Margin.NONE = 0
        margin: Margin.SMALL,
        // default is 'A4'
        // format: "letter",
        format: "A4",
        // default is 'portrait'
        // orientation: "landscape"
        orientation: "portrait",
      },
      canvas: {
        // default is 'image/jpeg' for better size performance
        mimeType: "image/jpeg",
        qualityRatio: 1,
      },
      // Customize any value passed to the jsPDF instance and html2canvas
      // function. You probably will not need this and things can break,
      // so use with caution.
      overrides: {
        // see https://artskydj.github.io/jsPDF/docs/jsPDF.html for more options
        pdf: {
          compress: true,
        },
        // see https://html2canvas.hertzen.com/configuration for more options
        canvas: {
          useCORS: true,
        },
      },
    };

  const [doctorNotesForSummary, setDoctorNotesForSummary] = useState(null);
  const [patientHistoryForSummary, setPatientHistoryForSummary] =
    useState(null);
  //--------------------

  const fetchPatientHealthData = async () => {
    const inputToFetch = {
      input: {
        patientid: patientId,
        doctorid: doctorId,
        appointmentid: null,
        // patientid: 1866,
        // doctorid: 454,
        // appointmentid: null,
      },
    };
    showLoader();
    try {
      const data = await GetPatientHealthData(inputToFetch.input);
      // console.log(data);
      setPatientHistoryForSummary(data);
    } catch (error) {
      console.error("Error fetching patient health data:", error);
    } finally {
      dismissLoader();
    }
  };

  const fetchDoctorNotesProgressNotes = async () => {
    const inputToFetch = {
      input: {
        patientid: patientId,
        doctorid: doctorId,
        appointmentid: appointmentId,
        // patientid: 1866,
        // doctorid: 454,
        // appointmentid: 3110,
      },
    };
    showLoader();
    try {
      // Sleep for 10 seconds
      // await new Promise((resolve) => setTimeout(resolve, 15000));

      const response = await GetDoctorNotes(inputToFetch.input);
      const data = response?.output?.data;
      // console.log(data);
      setDoctorNotesForSummary(data);
    } catch (error) {
      console.error("Error fetching Doctor Notes data:", error);
    } finally {
      dismissLoader();
    }
  };

  useEffect(() => {
    fetchDoctorNotesProgressNotes();
    fetchPatientHealthData();
  }, [patientId, doctorId, appointmentId]);

  const saveProgressNotesUpload = async (fileName) => {
    // const inputToSave = {
    //   input: {
    //     patientid: patientId,
    //     doctorid: doctorId,
    //     appointmentid: appointmentId,
    //     documenturl: fileName,
    //     type: "progressnotes",
    //     branchid: null,
    //   },
    // };
    const inputToSave = {
      input: {
        patientid: patientId,
        doctorid: doctorId,
        appointmentid: appointmentId,
        progressnoteurl: fileName,
        baseurl: BlobURL,
        doctorname: inputToPrescription.drName,
        patientname: inputToPrescription.patientName,
        patientmobile: inputToPrescription.patientPhone,
        patientmobilecountrycode: inputToPrescription.patientPhoneCountryCode
      },
    };
    console.log(inputToSave);
    try {
      const response = await UploadPrescription(inputToSave.input);
      // console.log(response);
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };

  const handleGeneratePDFToUpload = async () => {
    const pdfInstance = await generatePDF(getTargetElement, uploadOptions);
    return new Blob([pdfInstance.output("blob")], {
      type: "application/pdf",
    });
  };

  const uploadFileWithProgressNotes = async () => {
    try {
      const pdfBlob = await handleGeneratePDFToUpload();
      const file = new File([pdfBlob], "progressnotes.pdf", {
        type: "application/pdf",
      });
      const response = await uploadImageCommonFunction(file, "progressnotes.pdf");
      // console.log(response);
      if (response) {
        saveProgressNotesUpload(response.name);
      }
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };

  useEffect(() => {
      if (fromEndVisit) {
        const storedAppointmentId = localStorage.getItem(
          "progressNotesForAppointmentId"
        );
        if (storedAppointmentId !== appointmentId) {
          const uploadAfterFetch = async () => {
            await fetchDoctorNotesProgressNotes();
          };
          uploadAfterFetch();
        }
      }
    }, [fromEndVisit, appointmentId]);
  
    useEffect(() => {
      if (fromEndVisit && doctorNotesForSummary && patientHistoryForSummary) {
        const storedAppointmentId = localStorage.getItem(
          "progressNotesForAppointmentId"
        );
        console.log('storedAppoinemtneeid: ', storedAppointmentId)
        console.log('appointmentId: ', appointmentId)
        if (storedAppointmentId !== appointmentId) {
          console.log("Upload called progress notes");
          uploadFileWithProgressNotes();
          localStorage.setItem("progressNotesForAppointmentId", appointmentId);
        }else {
          console.log('store appointmentid not hit')
        }
      }
    }, [fromEndVisit, appointmentId, doctorNotesForSummary, patientHistoryForSummary]);

  return (
    <Box height="100vh" overflowY="scroll">
      {isLoading && <Loader />}
      <VStack spacing={4} justify="center" align="center" p={4}>
        <HStack spacing={4}>
          <PrimaryButton
            buttonText="Print"
            // variant="grayBtn"
            // btnStyle={{ w: "12rem", h: "3rem", color: COLORS.PRIMARY_COLOR }}
            btnStyle={{ w: "12rem", h: "3rem" }}
            onClick={handlePrint}
          />
          <PrimaryButton
            buttonText="Download"
            onClick={handleDownloadLocal}
            btnStyle={{ w: "12rem", h: "3rem" }}
          />
          {fromEndVisit && <PrimaryButton
            buttonText="Progress Notes"
            // variant="grayBtn"
            // btnStyle={{ w: "12rem", h: "3rem", color: COLORS.PRIMARY_COLOR }}
            btnStyle={{ w: "12rem", h: "3rem" }}
            onClick={handleProgressNotesClick}
          />}

        </HStack>
        <VStack align="center">
          <Box ref={componentRef} width="100%">
            <Prescription {...inputToPrescription} />
          </Box>
        </VStack>
      </VStack>

      <Box
              // ref={prescriptionRef}
              id="container"
              position="absolute"
              left="-9999px"
              width="800px"
            >

        {doctorNotesForSummary && patientHistoryForSummary && (
          <VisitSummary
            doctorNotes={doctorNotesForSummary}
            patientHistory={patientHistoryForSummary}
          />
        )}
            </Box>
    </Box>
  );
};

export default VisitPrescription;
